// Lib
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { cloneDeep, get, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';

// Utils
import { hasCommandModifier, isControlKeyCommand } from '../../../utils/keyboard/keyboardUtility';
import { createTableCellEntry } from '../utils/tableInitialisationUtils';
import {
    asCellSelection,
    getAllCellsBetween,
    getColRefHeaderCellSelection,
    getFirstSelection,
    getRowRefHeaderCellSelection,
    getStartCol,
    getStartRow,
    hasSelections,
    isSelectingSingleCell,
    isSelectingSingleSelection,
    fixSelectionBorderDisplay,
} from '../utils/tableCellSelectionUtils';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import {
    adjustTableContentDataSize,
    getColumnCount,
    getRowCount,
    getValueArray,
    shiftRowsOrColumns,
} from '../utils/tableDataUtils';
import { createCellDataChanges, createCellDataChangesFromSource } from '../utils/tableCellEditingUtils';
import { colorBorders, continueCellFormatting } from '../utils/tableCellFormattingUtils';
import HotTableClipboardManager from '../manager/HotTableClipboardManager';
import { inferFormulaCellType } from '../utils/tableCellTypeUtils';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import { updateRowHeights } from '../utils/tableSizeUtils';
import { getCurrentTransactionId, getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';

// Types
import { TableAxis, TableOperation } from '../../../../common/table/TableTypes';

// Constants
import { TABLE_DEFAULT_COL_WIDTH } from '../../../../common/table/tableConstants';
import { MILANOTE_EDITING_PLUGIN_NAME } from '../modules/MilanoteEditingPlugin';
import { HYBRID_APP_FAKE_CONTEXT_MENU_EVENT_DETAIL } from '../../../hybridApp/store/hybridAppStoreConstants';
import { TABLE_ELEMENT_CELL_SELECTIONS_UPDATE } from '../../../../common/elements/selectionConstants';

const TableCellEditingHandlers = forwardRef(function TableCellEditingHandlersComponent(props, ref) {
    const {
        element,

        tableContentColWidthsGU,
        tableContentData,
        currentCellSelections,

        hotTableInstanceRef,
        tableOperationsRef,

        dispatchUpdateTableElement,
        dispatchDeselectElement,

        dispatchUndoAction,
        dispatchRedoAction,
        dispatchFinishUndoingOrRedoing,

        gridSize,
        isSingleSelected,
        isUndoingOrRedoing,
        isDarkMode,
        showTitle,
        showCaption,
    } = props;

    const elementId = getElementId(element);

    // **********************************
    // EFFECTS
    // **********************************

    // Update showTitle value in the hotTableInstance
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.showTitle = showTitle;
    }, [showTitle]);

    // Update showCaption value in the hotTableInstance
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.showCaption = showCaption;
    }, [showCaption]);

    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.gridSize = gridSize;

        // Destroy the editor so that it can be recalculated in new grid size
        requestAnimationFrame(() => {
            hotTableInstanceRef.current.destroyEditor();
        });
    }, [gridSize]);

    // Update default row heights
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        updateRowHeights(hotTableInstanceRef.current);
    }, [showTitle, showCaption, gridSize]);

    // Update relevant values with new darkmode setting
    useEffect(() => {
        if (!hotTableInstanceRef.current) return;

        hotTableInstanceRef.current.milanoteProps.isDarkMode = isDarkMode;

        // recalculate the coloured cell borders with the new darkmode setting
        const dataArray = hotTableInstanceRef.current.getData();
        for (let row = 0; row < dataArray.length; row++) {
            for (let col = 0; col < dataArray[row].length; col++) {
                colorBorders(hotTableInstanceRef.current, row, col);
            }
        }
    }, [isDarkMode]);

    useEffect(() => {
        // Override Handsontable undo/redo functionality to use Milanote's instead

        if (!hotTableInstanceRef.current) return;

        const undoRedoPluginInstance = hotTableInstanceRef.current.getPlugin('undoRedo');
        undoRedoPluginInstance.undo = () => {
            if (hotTableInstanceRef.current.isListening()) {
                dispatchUndoAction();
            }
        };
        undoRedoPluginInstance.redo = () => {
            if (hotTableInstanceRef.current.isListening()) {
                dispatchRedoAction();
            }
        };
    }, []);

    // isUndoingOrRedoing state persists as true in the state, and causes issues where changes to cell is cleared
    // after an undo/redo operation. So, always toggle this state back to false after an undo/redo operation on table.
    useEffect(() => {
        if (isSingleSelected && isUndoingOrRedoing) dispatchFinishUndoingOrRedoing();
    }, [isSingleSelected, isUndoingOrRedoing]);

    /**
     * If table data in Redux is different to table data in Handsontable, update the table data in Handsontable to match.
     * This is required for undo/redo to work properly.
     */
    useEffect(() => {
        const milanoteEditingPluginInstance = hotTableInstanceRef.current.getPlugin(MILANOTE_EDITING_PLUGIN_NAME);
        const dataValues = milanoteEditingPluginInstance.getOriginalSourceDataArray();

        const tableContentDataValues = getValueArray(tableContentData);
        if (!isEqual(tableContentDataValues, dataValues)) {
            hotTableInstanceRef.current.updateData(tableContentDataValues);
        }

        let changes = [];
        tableContentDataValues.forEach((rowData, row) => {
            rowData.forEach((_, col) => {
                const cellData = get(tableContentData, [row, col]);
                const { cellData: hotCellData } = hotTableInstanceRef.current.getCellMeta(row, col);

                if (!isEqual(hotCellData, cellData)) {
                    tableOperationsRef.current.setCellMetaObject(row, col, { cellData });

                    changes.push([row, col, cellData?.value]);
                }
            });
        });

        if (!isEmpty(changes)) {
            hotTableInstanceRef.current.render();

            // This is required to ensure that the table is resized properly for the new content
            requestAnimationFrame(() => {
                hotTableInstanceRef.current.setDataAtCell(changes, 'CellMeta.init');
            });
        }
    }, [tableContentData]);

    // **********************************
    // HANDLERS
    // **********************************

    const beforeAutofill = useCallback((_, sourceRange) => {
        HotTableClipboardManager.setTableAutofillSource(hotTableInstanceRef, asCellSelection(sourceRange));
    });

    const beforeCopy = useCallback((data, coords) => {
        const { startRow, startCol, endRow, endCol } = getFirstSelection(coords);

        HotTableClipboardManager.setTableCopySource(hotTableInstanceRef, [startRow, startCol, endRow, endCol]);
    });

    const beforeCut = useCallback((data, coords) => {
        beforeCopy(data, coords);

        // Revert readOnly cells to be editable so that they can be cleared on cut
        const { startRow, startCol, endRow, endCol } = getFirstSelection(coords);
        getAllCellsBetween([[startRow, startCol, endRow, endCol]]).forEach(({ row, col }) => {
            hotTableInstanceRef.current.setCellMetaObject(row, col, { readOnly: false });
        });
    });

    const beforePaste = useCallback((data, coords) => {
        const { startRow, startCol } = getFirstSelection(coords);

        const nRows = getRowCount(data);
        const nCols = getColumnCount(data);

        // Revert readOnly cells to be editable so that they can be pasted on to
        const pastedCellSelection = [startRow, startCol, startRow + nRows, startCol + nCols];
        getAllCellsBetween([pastedCellSelection]).forEach(({ row, col }) => {
            hotTableInstanceRef.current.setCellMetaObject(row, col, { readOnly: false });
        });
    });

    const afterBeginEditing = useCallback(() => {
        fixSelectionBorderDisplay(hotTableInstanceRef.current);
    });

    const beforeChange = useCallback(
        (changes, source) => {
            // If source is coming from updating cell meta, save data as is
            if (source.startsWith('CellMeta')) return true;

            const data = tableContentData;

            // ********************************************
            // Convert cell changes [row, col, prevValue, newValue] to [row, col, prevCellData, newCellData]

            let cellDataChanges;
            if (source === 'Autofill.fill' || source === 'CopyPaste.paste') {
                const sourceCellDataArray =
                    source === 'Autofill.fill'
                        ? HotTableClipboardManager.getTableAutofillData()
                        : HotTableClipboardManager.getTableCopiedDataForChanges(changes);

                cellDataChanges = createCellDataChangesFromSource(changes, data, source, sourceCellDataArray);
            } else {
                cellDataChanges = createCellDataChanges(changes, data);
            }

            // ********************************************
            // Adjust table size to fit all the changes

            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            const maxChangeRow = Math.max(...cellDataChanges.map(([row]) => row)) + 1;
            const maxChangeCol = Math.max(...cellDataChanges.map(([_, col]) => col)) + 1;

            const maxRow = Math.max(maxChangeRow, nRows);
            const maxCol = Math.max(maxChangeCol, nCols);

            const newData = adjustTableContentDataSize(data, maxRow, maxCol);

            const nRowsNew = getRowCount(newData) - nRows;
            const nColsNew = getColumnCount(newData) - nCols;

            const shouldInsertRows = nRowsNew > 0;
            const shouldInsertCols = nCols > 0;

            const transactionId = getNewTransactionId();
            const sourceWithTransactionId = `${source}:${transactionId}`;

            if (shouldInsertRows) {
                hotTableInstanceRef.current.alter('insert_row_below', nRows - 1, nRowsNew, sourceWithTransactionId);
            }

            if (shouldInsertCols) {
                hotTableInstanceRef.current.alter('insert_col_end', nCols - 1, nColsNew, sourceWithTransactionId);
            }

            // ********************************************
            // Apply changes if any

            cellDataChanges.forEach((cellDataChange, index) => {
                const [row, col, , newCellData] = cellDataChange;

                changes[index][3] = newCellData.value;
                newData[row][col] = newCellData;

                tableOperationsRef.current.setCellMetaObject(row, col, { cellData: newCellData });
            });

            if (isEqual(newData, data)) return;

            const newColWidthsGU = newData[0].map(
                (_, index) => tableContentColWidthsGU[index] || TABLE_DEFAULT_COL_WIDTH,
            );

            const firstChange = changes[0];
            const [firstChangeRow, firstChangeCol] = firstChange;
            const undoRedoCellSelectionAction = {
                type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
                id: elementId,
                cellSelections:
                    changes.length === 1
                        ? [[firstChangeRow, firstChangeCol, firstChangeRow, firstChangeCol]]
                        : hotTableInstanceRef.current.getSelected(),
            };

            // Delay the update to allow the table to render the changes first
            setTimeout(() => {
                dispatchUpdateTableElement({
                    id: elementId,
                    changes: {
                        tableContent: {
                            data: newData,
                            colWidthsGU: newColWidthsGU,
                        },
                        width: newColWidthsGU.reduce((sum, val) => sum + val, 0),
                    },
                    batchUndoActions: [undoRedoCellSelectionAction],
                    batchRedoActions: [undoRedoCellSelectionAction],
                    transactionId,
                });
            }, 0);
        },
        [elementId, tableContentData, tableContentColWidthsGU],
    );

    /**
     * After a change is made, infer formula cell's cell type based on precedents cells (if it is not already set)
     */
    const afterChange = useCallback(
        (changes, source) => {
            if (
                !changes ||
                source === 'loadData' ||
                source === 'CellMeta.init' ||
                source === 'CellMeta.updateCellType.effect'
            )
                return;

            const sheet = hyperFormulaInstance.getSheetId(elementId);
            if (sheet === undefined) return;

            const data = cloneDeep(tableContentData);
            const visited = new WeakMap();

            const applyCellChangesForFormulas = (elementId, newChanges, cellAddress, level = 0) => {
                if (visited.get(cellAddress)) return;
                visited.set(cellAddress, true);

                const isDirectUpdateCellTypeChange = source === 'CellMeta.updateCellType' && level === 0;
                const isValidCellAddress = 'row' in cellAddress && 'col' in cellAddress;
                if (!isDirectUpdateCellTypeChange && isValidCellAddress) {
                    const { row, col } = cellAddress;

                    const inferredType = inferFormulaCellType(elementId, data, { row, col });
                    const currentType = data[row][col].type;

                    if (inferredType && inferredType.name !== currentType?.name) {
                        newChanges.push([row, col, () => ({ type: inferredType })]);
                        data[row][col].type = inferredType;
                    }
                }

                const cellDependents = hyperFormulaInstance.getCellDependents(cellAddress);
                cellDependents.forEach((cellDependent) => {
                    applyCellChangesForFormulas(elementId, newChanges, cellDependent, level + 1);
                });
            };

            const cellDataChanges = [];

            changes.forEach(([row, col]) =>
                applyCellChangesForFormulas(elementId, cellDataChanges, { sheet, row, col }),
            );

            if (cellDataChanges.length === 0) return;

            tableOperationsRef.current.setCellData(
                cellDataChanges,
                'CellMeta.updateCellType.effect',
                getCurrentTransactionId(),
            );
        },
        [elementId, tableContentData],
    );

    /**
     * This function is called we want to sync table data from Handsontable over to table data in Redux.
     */
    const afterGridAltered = useCallback((axis, operation, index, amount, source) => {
        // New data needs to be recreated from handsontable data, as formulas might've been updated after grid altered
        const milanoteEditingPluginInstance = hotTableInstanceRef.current.getPlugin(MILANOTE_EDITING_PLUGIN_NAME);
        const dataValues = milanoteEditingPluginInstance.getOriginalSourceDataArray();

        const newData = [];
        dataValues.forEach((rowValues, row) => {
            if (!newData[row]) newData.push([]);

            rowValues.forEach((value, col) => {
                const { cellData = createTableCellEntry() } = hotTableInstanceRef.current.getCellMeta(row, col);

                newData[row].push({ ...cellData, value });
            });
        });

        // Update col widths
        let newColWidthsGU = cloneDeep(tableContentColWidthsGU);
        if (axis === TableAxis.COL && operation === TableOperation.INSERT) {
            newColWidthsGU.splice(index, 0, ...new Array(amount).fill(TABLE_DEFAULT_COL_WIDTH));
        }
        if (axis === TableAxis.COL && operation === TableOperation.REMOVE) {
            newColWidthsGU.splice(index, amount);
        }

        // Get data with formatting applied
        const newDataWithFormatting = continueCellFormatting({
            dataArray: newData,
            ...(axis === TableAxis.COL &&
                operation === TableOperation.INSERT && {
                    colIndex: index,
                    nColsAdded: amount,
                }),
            ...(axis === TableAxis.ROW &&
                operation === TableOperation.INSERT && {
                    rowIndex: index,
                    nRowsAdded: amount,
                }),
            hotTableInstanceRef,
        });

        const transactionIdStr = get(source?.split(':'), 1);
        const transactionId = transactionIdStr && parseInt(transactionIdStr);

        dispatchUpdateTableElement({
            id: elementId,
            changes: {
                tableContent: {
                    data: newDataWithFormatting,
                    colWidthsGU: newColWidthsGU,
                },
                width: newColWidthsGU.reduce((sum, val) => sum + val, 0),
            },
            transactionId,
        });
    });

    // In Handsontable, moving rows/columns is treated as a temporary visual change, and only persist in the
    // session. The ordering is saved in `hotTableInstanceRef.current.rowIndexMapper.indexesSequence`.
    //
    // Since we want to persist the ordering to the DB, on every rows/columns move, we want to:
    // 1. Reset `hotTableInstanceRef.current.rowIndexMapper.indexesSequence` to its original form, so that the
    //    ordering in rowIndexMapper remains the same
    // 2. Apply the movement to the data in Redux instead, which will be applied to Handsontable
    const afterGridMovement = useCallback((axis, movedIndices, finalIndex, dropIndex, movePossible, orderChanged) => {
        if (!movePossible || !orderChanged) return;

        let newData = cloneDeep(tableContentData);
        let newColWidthsGU = cloneDeep(tableContentColWidthsGU);
        let movedIndicesCopy = cloneDeep(movedIndices);

        // New data needs to be recreated from handsontable data, as formulas might've been updated after grid altered
        const milanoteEditingPluginInstance = hotTableInstanceRef.current.getPlugin(MILANOTE_EDITING_PLUGIN_NAME);
        const dataValues = milanoteEditingPluginInstance.getOriginalSourceDataArray();
        dataValues.forEach((rowValues, row) => {
            rowValues.forEach((value, col) => {
                newData[row][col].value = value;
            });
        });

        const nRows = getRowCount(newData);
        const nCols = getColumnCount(newData);

        if (axis === TableAxis.ROW) {
            const originalIndexesSequence = new Array(nRows).fill(1).map((_, i) => i);
            hotTableInstanceRef.current.rowIndexMapper.setIndexesSequence(originalIndexesSequence);

            shiftRowsOrColumns(movedIndicesCopy, newData, finalIndex);
        }

        if (axis === TableAxis.COL) {
            const originalIndexesSequence = new Array(nCols).fill(1).map((_, i) => i);
            hotTableInstanceRef.current.columnIndexMapper.setIndexesSequence(originalIndexesSequence);

            newData.forEach((row) => shiftRowsOrColumns(movedIndicesCopy, row, finalIndex));
            shiftRowsOrColumns(movedIndicesCopy, newColWidthsGU, finalIndex);
        }

        // Get CellSelections on undo/redo
        const tableInfo = {
            nRows: hotTableInstanceRef.current.countRows(),
            nCols: hotTableInstanceRef.current.countCols(),
        };

        const getRefHeaderCellSelection =
            axis === TableAxis.ROW ? getRowRefHeaderCellSelection : getColRefHeaderCellSelection;

        const moveRangeStart = get(movedIndices, 0);
        const moveRangeEnd = get(movedIndices, 1) || get(movedIndices, 0);

        // Apply changes to Redux state

        dispatchUpdateTableElement({
            id: elementId,
            changes: {
                tableContent: {
                    data: newData,
                    colWidthsGU: newColWidthsGU,
                },
                width: newColWidthsGU.reduce((sum, val) => sum + val, 0),
            },
            batchUndoActions: [
                {
                    type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
                    id: elementId,
                    cellSelections: [getRefHeaderCellSelection(moveRangeStart, moveRangeEnd, tableInfo)],
                },
            ],
            batchRedoActions: [
                {
                    type: TABLE_ELEMENT_CELL_SELECTIONS_UPDATE,
                    id: elementId,
                    cellSelections: [
                        getRefHeaderCellSelection(finalIndex, finalIndex + moveRangeEnd - moveRangeStart, tableInfo),
                    ],
                },
            ],
        });

        requestAnimationFrame(() => {
            hotTableInstanceRef.current.deselectCell();

            const startSelectIndex = finalIndex;
            const endSelectIndex = finalIndex + movedIndicesCopy.length - 1;

            if (axis === TableAxis.ROW) hotTableInstanceRef.current.selectRows(startSelectIndex, endSelectIndex);
            if (axis === TableAxis.COL) hotTableInstanceRef.current.selectColumns(startSelectIndex, endSelectIndex);
        });
    });

    const beforeOnCellMouseDown = useCallback(
        (event, coords) => {
            const isRightMouseClick = event.button === 2;
            if (isRightMouseClick) return;

            // ************************************************
            // On Cmd+click, and no cells are selected, deselect element

            if ((hasCommandModifier(event) || isControlKeyCommand(event)) && !hasSelections(currentCellSelections)) {
                event.stopImmediatePropagation();
                event.preventDefault();

                dispatchDeselectElement(elementId);
                return;
            }

            // ************************************************
            // On clicking the selected cell one more time, immediately go to edit mode. This overrides the default
            // behaviour of requiring a double click to edit a cell, and is more in line with Milanote editing process.

            const isSelectingCurrentlySelectedCell =
                isSelectingSingleSelection(currentCellSelections) &&
                isSelectingSingleCell(getFirstSelection(currentCellSelections)) &&
                coords.row === getStartRow(getFirstSelection(currentCellSelections)) &&
                coords.col === getStartCol(getFirstSelection(currentCellSelections));

            if (
                !(hasCommandModifier(event) || isControlKeyCommand(event)) &&
                isSelectingCurrentlySelectedCell &&
                hotTableInstanceRef.current.getActiveEditor()
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();

                hotTableInstanceRef.current.getActiveEditor().beginEditing();
            }
        },
        [elementId, currentCellSelections],
    );

    const beforeOnCellContextMenu = useCallback((event, { row, col }) => {
        // When triggering context menu on an ipad using two finger tap, the context menu will be opened in the middle
        // of the two fingers, but a cell will be selected in one of the finger taps. We want to override this behaviour
        // by manually selecting the cell in the middle of the 2 finger tap.
        if (event.detail === HYBRID_APP_FAKE_CONTEXT_MENU_EVENT_DETAIL) {
            hotTableInstanceRef.current.selectCell(row, col);
        }
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            afterBeginEditing,
            beforeAutofill,
            beforeCopy,
            beforeCut,
            beforePaste,
            beforeChange,
            afterChange,
            afterGridAltered,
            afterGridMovement,
            beforeOnCellMouseDown,
            beforeOnCellContextMenu,
        }),
        [
            afterBeginEditing,
            beforeAutofill,
            beforeCopy,
            beforeCut,
            beforePaste,
            beforeChange,
            afterChange,
            afterGridAltered,
            afterGridMovement,
            beforeOnCellMouseDown,
            beforeOnCellContextMenu,
        ],
    );

    return null;
});

TableCellEditingHandlers.propTypes = {
    element: PropTypes.object,

    tableContentColWidthsGU: PropTypes.array,
    tableContentData: PropTypes.array,
    currentCellSelections: PropTypes.object,

    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    tableOperationsRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

    updateHotCellSelections: PropTypes.func,
    jumpToTitle: PropTypes.func,
    dispatchUpdateTableElement: PropTypes.func,
    dispatchDeselectElement: PropTypes.func,

    dispatchUndoAction: PropTypes.func,
    dispatchRedoAction: PropTypes.func,
    dispatchFinishUndoingOrRedoing: PropTypes.func,

    gridSize: PropTypes.number,
    isSingleSelected: PropTypes.bool,
    isUndoingOrRedoing: PropTypes.bool,
    isDarkMode: PropTypes.bool,
    showTitle: PropTypes.bool,
    showCaption: PropTypes.bool,

    getContextZoomScale: PropTypes.func,
};

export default TableCellEditingHandlers;
